/**
 * Référentiel composantes paie Gabon 2026 (COMPOSANTES + barème IRPP formule).
 *
 * AJOUTER UNE PRIME = ajouter une entrée dans COMPOSANTES.
 * Taux / plafonds sociaux = `src/shared/constants/Taux.ts` (source unique).
 *
 * Sources : CGI Gabon 2022 · Décret 0487/PR/MASI 18/12/2025 · Code du travail
 * Port fidèle de moteur_paie_gabon.py (Nexxus Conseil).
 */

import {
  PLAFOND_CNAMGS,
  PLAFOND_CNSS,
  PLAFOND_LOGEMENT_EXONERE,
  PLAFOND_TRANSPORT_EXONERE,
  PLAFOND_VEHICULE_EXONERE,
  SEUIL_EXONERATION_TCS,
  TAUX_CNAMGS_SAL,
  TAUX_CNSS_VID_SAL,
  TAUX_LOGEMENT_EXONERE,
  TAUX_TCS,
} from '@shared/constants/Taux';
import { CODES_RUBRIQUES } from './RubriquesPaie';

export type CategorieComposante = 'A' | 'B' | 'C' | 'D' | 'E';

export type ExoIrppGroupeKey = 'art91_11';

export interface ComposanteDef {
  categorie: CategorieComposante;
  label: string;
  /** True → entre dans base CNSS/CNAMGS/TCS (après exo_ss). */
  en_base_ss: boolean;
  /**
   * 0 → tout imposable en SS
   * N → fraction ≤ N exonérée
   * Infinity → tout exonéré de SS
   * null → calculé dynamiquement (logement_formule)
   */
  exo_ss_mensuel: number | null;
  logement_formule: boolean;
  /**
   * 0 → pas d'exo IRPP extra
   * Infinity → tout exonéré IRPP (cat D)
   * null → géré par exo_irpp_groupe
   */
  exo_irpp_extra: number | null;
  exo_irpp_groupe: ExoIrppGroupeKey | null;
  /**
   * Traitement de la part NON exonérée : `complet` (CNSS + CNAMGS + TCS) ou `cnss_seule`
   * (dans le brut et la base CNSS, hors CNAMGS et TCS — catégorie 2 des bulletins).
   */
  regime_imposable: 'complet' | 'cnss_seule';
  /** Code de rubrique du référentiel paie v2 (onglet RUBRIQUES) de la part imposable. */
  code: string;
  /** Code de rubrique de la part exonérée (34xxx : hors brut, ajoutée au net). */
  code_exonere?: string;
  ref_legale: string;
  note: string;
}

export interface ExoGroupeDef {
  label: string;
  /** Plafond ANNUEL et GLOBAL (toutes les primes du groupe, IRPP et TCS), à cumuler d'un mois à l'autre. */
  plafond_annuel: number;
  ref: string;
  note: string;
}

// ─── Constantes fiscales Gabon 2026 (alias Taux.ts — ne pas hardcoder) ───────
export const LOGEMENT_PLAFOND = PLAFOND_LOGEMENT_EXONERE; // CGI art.93
export const LOGEMENT_PCT = TAUX_LOGEMENT_EXONERE; // CGI art.93
export const CNSS_TAUX = TAUX_CNSS_VID_SAL; // salarié
export const CNAMGS_TAUX = TAUX_CNAMGS_SAL; // salarié
export const TCS_TAUX = TAUX_TCS; // CGI art.349
export const TCS_SEUIL = SEUIL_EXONERATION_TCS;
export const CNSS_PLAFOND = PLAFOND_CNSS;
/** Plafond mensuel CNAMGS (2 500 000 — Excel référentiel). */
export const CNAMGS_PLAFOND = PLAFOND_CNAMGS;

export const CAT_LABELS: Record<CategorieComposante, string> = {
  A: 'A — Pleinement imposable',
  B: 'B — Exo partielle ou CNSS seule',
  C: 'C — (réservée)',
  D: 'D — Exonéré toutes charges',
  E: 'E — Régime spécial hors paie courante',
};

export const EXO_GROUPES: Record<ExoIrppGroupeKey, ExoGroupeDef> = {
  art91_11: {
    label: 'Groupe art.91§11 (rendement, perf., gratif., 13e mois, bonus)',
    plafond_annuel: 4_000_000,
    ref: 'CGI 2025 art.91 11° et art.347 al.2',
    note:
      'Toutes ces primes PARTAGENT le plafond de 4 000 000 FCFA, ANNUEL et GLOBAL (IRPP et TCS) : il se cumule ' +
      "d'un mois à l'autre, il n'est pas divisé par 12. Ex. : 13e mois de 1 500 000 en décembre après 3 000 000 de " +
      'primes exonérées depuis janvier → 1 000 000 exonérés, 500 000 imposables.',
  },
};

/**
 * COMPOSANTES — référentiel unique.
 * Ajouter une prime = une entrée ici. Zéro autre modification.
 */
export const COMPOSANTES: Record<string, ComposanteDef> = {
  // ── Catégorie A : Pleinement imposable ────────────────────────────────────
  anciennete: {
    categorie: 'A',
    label: "Prime d'ancienneté",
    en_base_ss: true,
    exo_ss_mensuel: 0,
    logement_formule: false,
    exo_irpp_extra: 0,
    exo_irpp_groupe: null,
    regime_imposable: 'complet',
    code: CODES_RUBRIQUES.PRIME_ANCIENNETE,
    ref_legale: 'Code du travail art.79',
    note: '2 % par an dès 2 ans, plafond 30 % (15 ans). Entièrement imposable.',
  },
  heures_sup: {
    categorie: 'A',
    label: 'Heures supplémentaires',
    en_base_ss: true,
    exo_ss_mensuel: 0,
    logement_formule: false,
    exo_irpp_extra: 0,
    exo_irpp_groupe: null,
    regime_imposable: 'complet',
    code: CODES_RUBRIQUES.HEURES_SUP,
    ref_legale: 'Code du travail art.147',
    note: 'Majorations légales : +15 % (nuit/dim.), +40 % (jours fériés). Entièrement imposable.',
  },
  avantage_vehicule: {
    categorie: 'A',
    label: 'Avantage en nature — Véhicule',
    en_base_ss: true,
    exo_ss_mensuel: 0,
    logement_formule: false,
    exo_irpp_extra: 0,
    exo_irpp_groupe: null,
    regime_imposable: 'complet',
    code: CODES_RUBRIQUES.AVANTAGE_NATURE,
    ref_legale: 'CGI art.86',
    note: 'Valorisation réelle ou forfaitaire. Toutes assiettes.',
  },
  avantage_repas: {
    categorie: 'A',
    label: 'Avantage en nature — Repas (cantine)',
    en_base_ss: true,
    exo_ss_mensuel: 0,
    logement_formule: false,
    exo_irpp_extra: 0,
    exo_irpp_groupe: null,
    regime_imposable: 'complet',
    code: CODES_RUBRIQUES.AVANTAGE_NATURE,
    ref_legale: 'CGI art.86',
    note: 'Avantage repas fourni par employeur (cantine). Toutes assiettes.',
  },
  conges_payes: {
    categorie: 'A',
    label: 'Indemnité de congés payés',
    en_base_ss: true,
    exo_ss_mensuel: 0,
    logement_formule: false,
    exo_irpp_extra: 0,
    exo_irpp_groupe: null,
    regime_imposable: 'complet',
    code: CODES_RUBRIQUES.AUTRES_IMPOSABLES,
    ref_legale: 'Code du travail art.209',
    note: 'Entièrement imposable toutes assiettes.',
  },
  // ── Catégorie B : Exonération partielle ───────────────────────────────────
  transport: {
    categorie: 'B',
    label: 'Prime de transport',
    en_base_ss: true,
    exo_ss_mensuel: PLAFOND_TRANSPORT_EXONERE,
    logement_formule: false,
    exo_irpp_extra: 0,
    exo_irpp_groupe: null,
    regime_imposable: 'cnss_seule',
    code: CODES_RUBRIQUES.PRIME_TRANSPORT_CNSS,
    code_exonere: CODES_RUBRIQUES.INDEMNITE_TRANSPORT,
    ref_legale: 'CGI art.91bis',
    note:
      'CGI art.91bis : 5 000 FCFA/j (4 voyages) ou 2 500 FCFA/j (2 voyages). ' +
      `Pratique DGI : ${PLAFOND_TRANSPORT_EXONERE.toLocaleString('fr-FR')} FCFA/mois. Fraction > plafond → base imposable.`,
  },
  indem_vehicule: {
    categorie: 'B',
    label: "Indemnité d'entretien véhicule (voiture de fonction personnelle)",
    en_base_ss: true,
    exo_ss_mensuel: PLAFOND_VEHICULE_EXONERE,
    logement_formule: false,
    exo_irpp_extra: 0,
    exo_irpp_groupe: null,
    regime_imposable: 'cnss_seule',
    code: CODES_RUBRIQUES.INDEMNITE_VEHICULE_CNSS,
    code_exonere: CODES_RUBRIQUES.INDEMNITE_VEHICULE,
    ref_legale: 'CGI art.91bis',
    note:
      'Indemnité forfaitaire versée au salarié utilisant son propre véhicule à des fins ' +
      'professionnelles (carburant, entretien, assurance). ' +
      "Distincte du remboursement kilométrique (art.91§2) et de l'avantage en nature véhicule (art.86). " +
      `Exonération plafonnée à ${PLAFOND_VEHICULE_EXONERE.toLocaleString('fr-FR')} FCFA/mois (CGI art.91bis). ` +
      `Fraction > ${PLAFOND_VEHICULE_EXONERE.toLocaleString('fr-FR')} FCFA intègre la base CNSS/CNAMGS/TCS/IRPP.`,
  },
  rendement: {
    categorie: 'B',
    label: 'Prime de rendement / performance',
    en_base_ss: true,
    exo_ss_mensuel: 0,
    logement_formule: false,
    exo_irpp_extra: null,
    exo_irpp_groupe: null,
    regime_imposable: 'complet',
    code: CODES_RUBRIQUES.PRIME_ECHANTILLONS,
    ref_legale: 'Référentiel paie v2 (bulletins réels 2026), rubrique 11720',
    note:
      'Pleinement imposable (CNSS, CNAMGS, TCS, IRPP) : la rubrique 11720 « prime échantillons » est imposable à la TCS et ' +
      "à l'IRPP sur les bulletins réels de 2026 (référentiel v2, plus récent que le CGI 2025) ; elle n'entre donc PAS dans " +
      "le plafond annuel de l'art. 91 11°. Pour une prime de performance, un bonus ou une gratification exonérés, utiliser " +
      'la gratification / 13e mois.',
  },
  gratification: {
    categorie: 'B',
    label: 'Gratification / 13e mois / bonus',
    en_base_ss: true,
    exo_ss_mensuel: 0,
    logement_formule: false,
    exo_irpp_extra: null,
    exo_irpp_groupe: 'art91_11',
    regime_imposable: 'complet',
    code: CODES_RUBRIQUES.PRIME_EXTRA,
    ref_legale: 'CGI art.91§11',
    note:
      'Même régime IRPP que prime rendement. ' +
      'PARTAGE le plafond annuel de 4 000 000 FCFA avec toutes les primes §11 de l\'année.',
  },
  responsabilite: {
    categorie: 'B',
    label: 'Indemnité de responsabilité (15270)',
    en_base_ss: true,
    exo_ss_mensuel: 0,
    logement_formule: false,
    exo_irpp_extra: 0,
    exo_irpp_groupe: null,
    regime_imposable: 'cnss_seule',
    code: CODES_RUBRIQUES.INDEMNITE_RESPONSABILITE,
    ref_legale: 'Référentiel paie v2 (bulletins)',
    note: 'Rubrique 15270 : base CNSS, hors base CNAMGS et hors base TCS (constaté sur les bulletins).',
  },
  // ── Catégorie C : Formule logement ────────────────────────────────────────
  logement: {
    categorie: 'B',
    label: 'Indemnité de logement (15000)',
    en_base_ss: true,
    exo_ss_mensuel: 0,
    logement_formule: false,
    exo_irpp_extra: 0,
    exo_irpp_groupe: null,
    regime_imposable: 'cnss_seule',
    code: CODES_RUBRIQUES.INDEMNITE_LOGEMENT,
    ref_legale: 'CGI art.93',
    note:
      'Indemnité de logement en espèces (rubrique 15000) : traitée à 100 % comme sur les bulletins ' +
      'de référence — base CNSS, hors base CNAMGS et hors base TCS. Plus d’exonération 40 % / 250 000.',
  },
  // ── Catégorie D : Exonéré toutes charges ─────────────────────────────────
  representation: {
    categorie: 'B',
    label: 'Indemnité de représentation (15320)',
    en_base_ss: true,
    exo_ss_mensuel: 0,
    logement_formule: false,
    exo_irpp_extra: 0,
    exo_irpp_groupe: null,
    regime_imposable: 'cnss_seule',
    code: CODES_RUBRIQUES.INDEMNITE_REPRESENTATION,
    ref_legale: 'CGI art.91§2',
    note:
      'Rubrique 15320 : comme sur les bulletins de référence, dans le brut et la base CNSS, ' +
      'hors base CNAMGS et hors base TCS.',
  },
  kilometrique: {
    categorie: 'D',
    label: 'Indemnité kilométrique',
    en_base_ss: false,
    exo_ss_mensuel: Number.POSITIVE_INFINITY,
    logement_formule: false,
    exo_irpp_extra: Number.POSITIVE_INFINITY,
    exo_irpp_groupe: null,
    regime_imposable: 'complet',
    code: CODES_RUBRIQUES.INDEMNITE_VEHICULE,
    code_exonere: CODES_RUBRIQUES.INDEMNITE_VEHICULE,
    ref_legale: 'CGI art.91§2',
    note: 'Remboursement frais de déplacement sur barème officiel — exonéré si justifié.',
  },
  ticket_repas: {
    categorie: 'D',
    label: 'Ticket restaurant / titre-repas',
    en_base_ss: false,
    exo_ss_mensuel: Number.POSITIVE_INFINITY,
    logement_formule: false,
    exo_irpp_extra: Number.POSITIVE_INFINITY,
    exo_irpp_groupe: null,
    regime_imposable: 'complet',
    code: CODES_RUBRIQUES.INDEMNITE_BLANCHISSAGE,
    code_exonere: CODES_RUBRIQUES.INDEMNITE_BLANCHISSAGE,
    ref_legale: 'CGI art.91§2',
    note: 'Exonéré dans la limite du raisonnable — frais réels de repas justifiés.',
  },
  // ── Catégorie E : Régime spécial hors paie courante ───────────────────────
  indem_licenciement: {
    categorie: 'E',
    label: 'Indemnité de licenciement',
    en_base_ss: false,
    exo_ss_mensuel: Number.POSITIVE_INFINITY,
    logement_formule: false,
    exo_irpp_extra: Number.POSITIVE_INFINITY,
    exo_irpp_groupe: null,
    regime_imposable: 'complet',
    code: CODES_RUBRIQUES.AUTRES_IMPOSABLES,
    ref_legale: 'CGI art.91§6 + Code travail',
    note:
      'Fraction légale exonérée ; excédent IRPP imposable. ' +
      'Hors paie mensuelle courante — calcul spécifique selon ancienneté.',
  },
  indem_retraite: {
    categorie: 'E',
    label: 'Indemnité de départ en retraite',
    en_base_ss: false,
    exo_ss_mensuel: Number.POSITIVE_INFINITY,
    logement_formule: false,
    exo_irpp_extra: Number.POSITIVE_INFINITY,
    exo_irpp_groupe: null,
    regime_imposable: 'complet',
    code: CODES_RUBRIQUES.AUTRES_IMPOSABLES,
    ref_legale: 'CGI art.91§5',
    note: 'Fraction légale exonérée. Hors paie mensuelle courante.',
  },
};
